import { createAsyncThunk } from "@reduxjs/toolkit";
import { CommonRootState } from "@common/types/common-root-state-type";
import { DBReminderThunks } from "./DBServiceThunks";
import { subscriptionPopupActions } from "./SubscriptionPopupReducer";
import { Reminder } from "@common-models/reminder";

/** The `message` of a rejected DB patch's payload (the server's Feathers error body) or its serialized error. */
const errorMessageOf = (payload: unknown): string | undefined => {
    if (typeof payload !== 'object' || payload === null) return undefined;
    const message: unknown = Reflect.get(payload, 'message');
    return typeof message === 'string' && message.length > 0 ? message : undefined;
};

interface CreateReminderParams {
    reminder: Reminder;
}

interface PatchReminderParams {
    reminder: Reminder;
    options?: { ignoreOriginalEntity: boolean };
}

// Create new reminder with CRM limit error handling
export const createReminderThunk = createAsyncThunk<
    Reminder,
    CreateReminderParams,
    { state: CommonRootState }
>(
    'reminders/createWithLimitCheck',
    async ({ reminder }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        // #1136: the create REJECTS with a typed DBCreateError when nothing was stored.
        const action = await dispatch(DBReminderThunks.create(reminder));
        if (DBReminderThunks.create.fulfilled.match(action)) {
            return action.payload;
        }

        const errorMessage = action.payload?.message ?? action.error.message ?? 'Failed to create reminder';
        console.error('[reminders-thunks] Server validation error:', action.payload ?? action.error);

        // Check if error is due to CRM limit reached
        if (errorMessage.includes('active reminders limit') || errorMessage.includes('reminders limit')) {
            dispatch(subscriptionPopupActions.openPopup({
                feature: 'reminders',
                message: errorMessage,
                upgradeReason: 'reminders_limit'
            }));
        }
        throw new Error(errorMessage);
    }
);

// Patch existing reminder with CRM limit error handling
export const patchReminderThunk = createAsyncThunk<
    Reminder,
    PatchReminderParams,
    { state: CommonRootState }
>(
    'reminders/patchWithLimitCheck',
    async ({ reminder, options }, thunkApi) => {
        const dispatch = thunkApi.dispatch;

        // #1257: the patch REJECTS with the server's error body when nothing was saved.
        const action = await dispatch(DBReminderThunks.patch({
            entity: reminder,
            options: options || { ignoreOriginalEntity: false }
        }));
        if (DBReminderThunks.patch.fulfilled.match(action)) {
            return action.payload;
        }

        const errorMessage = errorMessageOf(action.payload) ?? errorMessageOf(action.error) ?? 'Failed to update reminder';
        console.error('[reminders-thunks] Server validation error:', action.payload ?? action.error);

        // Check if error is due to CRM limit reached
        if (errorMessage.includes('active reminders limit') || errorMessage.includes('reminders limit')) {
            dispatch(subscriptionPopupActions.openPopup({
                feature: 'reminders',
                message: errorMessage,
                upgradeReason: 'reminders_limit'
            }));
        }
        throw new Error(errorMessage);
    }
);
